import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, ChevronRight, Home } from 'lucide-react';
import { useAuthStore } from '@shared/stores/authStore';
import { usePropertyStore } from '@shared/stores/propertyStore';
import { fetchMyProperties, type MemberProperty } from '@features/properties/api';
import { cn } from '@shared/lib/cn';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

/** Bare identifiers ("111", "12A") need the word; descriptive labels already read fine. */
function propertyTitle(unitLabel: string): string {
  const label = unitLabel.trim();
  return /^\d[\d./-]*[A-Za-z]?$/.test(label) ? `Unit ${label}` : label;
}

/** Two-line header title block: property label over the estate/community name. */
function TitleLines({
  title,
  subtitle,
  chevron,
}: {
  title: string;
  subtitle?: string;
  chevron?: ReactNode;
}): JSX.Element {
  return (
    <span className="flex min-w-0 flex-col items-start">
      <span className="flex min-w-0 max-w-full items-center gap-1">
        <MarqueeText className="text-body font-semibold leading-5 text-white">{title}</MarqueeText>
        {chevron}
      </span>
      {subtitle ? (
        <MarqueeText className="max-w-full text-caption font-normal leading-4 text-white/60">
          {subtitle}
        </MarqueeText>
      ) : null}
    </span>
  );
}

/**
 * Header property title — the property is the header's title: a two-line block
 * (property label over estate name) that opens a switcher popover when the
 * member has more than one approved property. Only rendered when the properties
 * module is enabled.
 */
export function PropertySwitcher(): JSX.Element | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const memberId = useAuthStore((s) => s.user?.member_id);
  const tenantId = useAuthStore((s) => s.tenantId);
  const activePropertyId = usePropertyStore((s) => s.activePropertyId);
  const setActivePropertyId = usePropertyStore((s) => s.setActivePropertyId);
  const syncActiveProperty = usePropertyStore((s) => s.syncActiveProperty);

  const propertiesQuery = useQuery({
    queryKey: ['my-properties', memberId],
    queryFn: fetchMyProperties,
    enabled: !!memberId,
  });

  const properties = propertiesQuery.data ?? [];

  useEffect(() => {
    if (!memberId || !tenantId || properties.length === 0) return;
    syncActiveProperty(properties, { memberId, tenantId });
  }, [properties, memberId, tenantId, syncActiveProperty]);

  const activeProperty = useMemo(() => {
    if (properties.length === 0) return null;
    const match = activePropertyId
      ? properties.find((p) => p.property_id === activePropertyId)
      : undefined;
    return match ?? properties[0];
  }, [properties, activePropertyId]);

  const title = propertyTitle(activeProperty?.unit_label ?? 'My residence');
  const estateName = activeProperty?.estate_name;
  const canSwitch = properties.length > 1;

  if (propertiesQuery.isLoading) {
    return (
      <span className="flex min-h-11 min-w-0 flex-col justify-center gap-1.5" aria-hidden>
        <span className="h-3.5 w-24 animate-pulse rounded bg-white/25" />
        <span className="h-2.5 w-16 animate-pulse rounded bg-white/20" />
      </span>
    );
  }

  // No approved property yet — point at the claim flow rather than an empty block.
  if (properties.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate('/app/my-properties')}
        className="flex min-h-11 min-w-0 items-center gap-1 rounded-lg px-1 text-left transition-colors hover:bg-white/10"
      >
        <MarqueeText className="text-body font-semibold text-white">Claim a property</MarqueeText>
        <ChevronRight className="h-4 w-4 shrink-0 text-white/70" strokeWidth={2} aria-hidden />
      </button>
    );
  }

  // Exactly one property — tappable to view it, but no switcher to fake.
  if (!canSwitch) {
    return (
      <button
        type="button"
        onClick={() => navigate('/app/my-properties')}
        aria-label={`View property, ${title}`}
        className="flex min-h-11 min-w-0 max-w-[12rem] items-center rounded-lg px-1 text-left transition-colors hover:bg-white/10"
      >
        <TitleLines
          title={title}
          subtitle={estateName}
          chevron={
            <ChevronRight className="h-4 w-4 shrink-0 text-white/70" strokeWidth={2} aria-hidden />
          }
        />
      </button>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={`Switch property, currently ${title}`}
          className={cn(
            'flex min-h-11 min-w-0 max-w-[12rem] items-center rounded-lg px-1 text-left transition-colors',
            open ? 'bg-white/15' : 'hover:bg-white/10',
          )}
        >
          <TitleLines
            title={title}
            subtitle={estateName}
            chevron={
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-white/80 transition-transform',
                  open && 'rotate-180',
                )}
                strokeWidth={2}
                aria-hidden
              />
            }
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-[60] min-w-[var(--radix-popover-trigger-width)] max-w-[min(18rem,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border border-white/25 bg-black/45 p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <p className="px-3 pb-1 pt-1.5 text-sm font-bold text-white/85">
            Active property
          </p>
          <ul role="listbox" aria-label="Select property" className="max-h-[min(16rem,50dvh)] overflow-y-auto">
            {properties.map((property) => (
              <li key={property.property_id}>
                <PropertyOption
                  property={property}
                  selected={property.property_id === activeProperty?.property_id}
                  onSelect={() => {
                    if (memberId && tenantId) {
                      setActivePropertyId(property.property_id, { memberId, tenantId });
                    } else {
                      setActivePropertyId(property.property_id);
                    }
                    setOpen(false);
                  }}
                />
              </li>
            ))}
          </ul>
          <div className="mt-1 border-t border-white/15 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/app/my-properties');
              }}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-body-sm font-medium text-white/90 transition-colors hover:bg-white/10"
            >
              <Home className="h-4 w-4 shrink-0 text-white/70" strokeWidth={1.75} />
              Manage properties
            </button>
          </div>
          <Popover.Arrow className="fill-black/45" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function PropertyAvatar(): JSX.Element {
  return (
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-white/20 text-white"
      aria-hidden
    >
      <Home className="h-4 w-4" strokeWidth={2} />
    </span>
  );
}

function PropertyOption({
  property,
  selected,
  onSelect,
}: {
  property: MemberProperty;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const subline = [property.estate_name, property.type_name].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left outline-none transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/25',
        selected ? 'bg-white/18' : 'hover:bg-white/10',
      )}
    >
      <PropertyAvatar />
      <span className="min-w-0 flex-1">
        <MarqueeText className="text-body-sm font-semibold text-white">{property.unit_label}</MarqueeText>
        {subline ? (
          <MarqueeText className="mt-0.5 text-caption text-white/60">{subline}</MarqueeText>
        ) : property.address ? (
          <MarqueeText className="mt-0.5 text-caption text-white/60">{property.address}</MarqueeText>
        ) : null}
      </span>
      {selected ? <Check className="h-4 w-4 shrink-0 text-brass" strokeWidth={2.5} /> : null}
    </button>
  );
}
